/**
 * FeatureGate Component
 * 
 * Wraps features that require tier access.
 * Shows a locked overlay with appropriate upgrade prompt for unavailable features.
 * 
 * UX Rules:
 * - Locked features appear disabled/read-only (not hidden by default)
 * - Starter users see "Upgrade to Pro" CTA
 * - Pro users see "Contact us" CTA for Studio features
 * - Studio/Legacy users see NO upgrade CTAs (self-serve blocked)
 * - Pending upgrades show "Upgrade pending confirmation"
 */

import { Lock, Sparkles, Clock, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { useFeatureGate, FEATURE_INFO } from "../hooks/useFeatureGate";

/**
 * FeatureGate - Wraps content with tier access control
 * 
 * @param {Object} props
 * @param {string} props.feature - The feature key to check
 * @param {React.ReactNode} props.children - Content to render if feature is available
 * @param {string} props.fallback - Custom fallback message (optional)
 * @param {boolean} props.hideCompletely - If true, hides content entirely instead of showing locked state
 */
export function FeatureGate({ feature, children, fallback, hideCompletely = false }) {
  const { hasFeature, getUpgradeCTA, getFeatureInfo, isUpgradePending } = useFeatureGate();
  const navigate = useNavigate();
  
  const isAvailable = hasFeature(feature);
  
  if (isAvailable) {
    return children;
  }
  
  if (hideCompletely) {
    return null;
  }
  
  const cta = getUpgradeCTA(feature);
  const featureInfo = getFeatureInfo(feature);
  
  // Determine icon and button based on CTA type
  const renderCTA = () => {
    if (cta.type === 'pending') {
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-3">
            <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <p className="font-medium mb-1">{featureInfo.name}</p>
          <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
            {cta.label}
          </p>
        </>
      );
    }
    
    if (cta.type === 'contact') {
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <Lock className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="font-medium mb-1">{featureInfo.name}</p>
          <p className="text-sm text-muted-foreground mb-3">
            {fallback || "Available on Studio plan"}
          </p>
          <Button 
            size="sm" 
            onClick={() => navigate("/studio-contact")}
            variant="outline"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            {cta.label}
          </Button>
        </>
      );
    }
    
    if (cta.type === 'upgrade') {
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <Lock className="w-6 h-6 text-muted-foreground" />
          </div>
          <p className="font-medium mb-1">{featureInfo.name}</p>
          <p className="text-sm text-muted-foreground mb-3">
            {fallback || cta.label}
          </p>
          <Button 
            size="sm" 
            onClick={() => navigate("/pricing")}
            className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {cta.label}
          </Button>
        </>
      );
    }
    
    // type === 'none' - Studio/Legacy users see locked but no CTA
    return (
      <>
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
          <Lock className="w-6 h-6 text-muted-foreground" />
        </div>
        <p className="font-medium mb-1">{featureInfo.name}</p>
        <p className="text-sm text-muted-foreground">
          {fallback || "This feature is not available"}
        </p>
      </>
    );
  };
  
  return (
    <div className="relative">
      {/* Grayed out content */}
      <div className="opacity-40 pointer-events-none select-none blur-[1px]">
        {children}
      </div>
      
      {/* Lock overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[2px] rounded-lg">
        <div className="text-center p-4">
          {renderCTA()}
        </div>
      </div>
    </div>
  );
}

/**
 * FeatureButton - A button that shows locked state for unavailable features
 */
export function FeatureButton({ 
  feature, 
  children, 
  onClick, 
  disabled,
  ...props 
}) {
  const { hasFeature, getUpgradeCTA } = useFeatureGate();
  const navigate = useNavigate();
  
  const isAvailable = hasFeature(feature);
  const cta = getUpgradeCTA(feature);
  
  if (!isAvailable) {
    // Pending upgrade state
    if (cta.type === 'pending') {
      return (
        <Button
          {...props}
          disabled
          variant="outline"
          className="relative opacity-70"
        >
          <Clock className="w-4 h-4 mr-2" />
          {children}
          <span className="ml-2 text-xs text-amber-600">
            (pending)
          </span>
        </Button>
      );
    }
    
    // Contact for Studio features
    if (cta.type === 'contact') {
      return (
        <Button
          {...props}
          onClick={() => navigate("/studio-contact")}
          variant="outline"
        >
          <Lock className="w-4 h-4 mr-2" />
          {children}
          <span className="ml-2 text-xs text-muted-foreground">
            (Contact us)
          </span>
        </Button>
      );
    }
    
    // Upgrade available
    if (cta.type === 'upgrade') {
      return (
        <Button
          {...props}
          onClick={() => navigate("/pricing")}
          variant="outline"
        >
          <Lock className="w-4 h-4 mr-2" />
          {children}
          <span className="ml-2 text-xs text-muted-foreground">
            (Pro)
          </span>
        </Button>
      );
    }
    
    // No CTA (Studio/Legacy) - show disabled
    return (
      <Button
        {...props}
        disabled
        variant="outline"
        className="opacity-60"
      >
        <Lock className="w-4 h-4 mr-2" />
        {children}
      </Button>
    );
  }
  
  return (
    <Button {...props} onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
}

/**
 * FeatureNavItem - Navigation item with locked state for unavailable features
 */
export function FeatureNavItem({ 
  feature, 
  to, 
  icon: Icon, 
  label,
  isActive,
  onClick 
}) {
  const { hasFeature, getUpgradeCTA } = useFeatureGate();
  const navigate = useNavigate();
  
  const isAvailable = hasFeature(feature);
  const cta = getUpgradeCTA(feature);
  
  const handleClick = (e) => {
    if (!isAvailable) {
      e.preventDefault();
      
      // Pending - do nothing
      if (cta.type === 'pending') {
        return;
      }
      
      // Contact - go to studio contact
      if (cta.type === 'contact') {
        navigate("/studio-contact");
        return;
      }
      
      // Upgrade - go to pricing
      if (cta.type === 'upgrade') {
        navigate("/pricing");
        return;
      }
      
      // No CTA - do nothing
      return;
    }
    if (onClick) onClick(e);
  };
  
  // Determine icon to show on right side
  const getRightIcon = () => {
    if (isAvailable) return null;
    
    if (cta.type === 'pending') {
      return <Clock className="w-4 h-4 text-amber-500" />;
    }
    
    return <Lock className="w-4 h-4 text-muted-foreground" />;
  };
  
  return (
    <a
      href={to}
      onClick={handleClick}
      className={`
        flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
        ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'}
        ${!isAvailable ? 'opacity-60 cursor-not-allowed' : ''}
      `}
    >
      <Icon className="w-5 h-5" />
      <span className="flex-1">{label}</span>
      {getRightIcon()}
    </a>
  );
}

/**
 * FeatureIndicator - Small inline indicator for feature availability
 * Use this for subtle feature status in lists or tables
 */
export function FeatureIndicator({ feature, showLabel = false }) {
  const { hasFeature, getUpgradeCTA, isUpgradePending } = useFeatureGate();
  
  if (hasFeature(feature)) {
    return null;
  }
  
  const cta = getUpgradeCTA(feature);
  
  if (cta.type === 'pending') {
    return (
      <span className="inline-flex items-center text-xs text-amber-600">
        <Clock className="w-3 h-3 mr-1" />
        {showLabel && "Pending"}
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center text-xs text-muted-foreground">
      <Lock className="w-3 h-3 mr-1" />
      {showLabel && (cta.type === 'upgrade' ? 'Pro' : cta.type === 'contact' ? 'Studio' : 'Locked')}
    </span>
  );
}

export default FeatureGate;
